// NavbarPos.tsx — Co Marca — Navbar del POS
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    Building, User, ArrowLeft, Banknote, X,
    PiggyBank, AlertCircle, ChevronDown, LogOut,
    DollarSign, QrCode, CreditCard as Card, Wallet, CalendarClock, KeyRound
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import {
    activarOperadorPOS, fetchOperadoresPOS, obtenerSesionActivaPorId,
    OperadorPOS, OperadorPOSGrant, SesionActivaResponse,
} from '../../../services/pos/sesionPOSService';
import { useNavigate } from 'react-router-dom';
import GestionMovimientosCaja from './GestionMovimientosCaja';
import { fetchPagosPorSesionPOS, Pago } from '../../../services/pagosService';
import NotificacionesPos from './notificacionespos/notificacionespos';
import TodayOccupiedSpacesModal from './TodayOccupiedSpacesModal';
import SolicitudReservaPOSModal from './SolicitudReservaPOSModal';

// ─────────────────────────────────────────────
// Design system
// ─────────────────────────────────────────────
const C = {
    primary: '#FF2052',
    primaryDark: '#9B004A',
    secondary: '#007FC8',
    secondaryDark: '#004d7a',
    secondaryLight: '#00DBB9',
    accent: '#FFD97A',
    accentDark: '#EC8400',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.nbp{flex-shrink:0;min-width:0}.nbp-actions{min-width:0;flex-wrap:wrap}
@media(max-width:1024px){.nbp{height:auto!important;min-height:60px;gap:12px;padding:12px!important;flex-wrap:wrap}.nbp-actions{max-width:100%;gap:6px!important}}
@media(max-width:420px){.nbp-actions{justify-content:flex-start!important;gap:6px!important}.nbp .nbp-nav-btn,.nbp .nbp-nav-btn.nbp-labeled,.nbp .nbp-user{min-width:40px;width:40px;height:44px}.nbp-user-wrap{margin-left:auto}}


.nbp * { font-family:'DM Sans',sans-serif !important; box-sizing:border-box; }
@keyframes nbpSpin  { to{transform:rotate(360deg);} }
@keyframes nbpPulse { 0%,100%{transform:scale(1);}50%{transform:scale(1.15);} }
@keyframes nbpSlideDown { from{opacity:0;transform:translateY(-8px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes nbpFadeIn { from{opacity:0;}to{opacity:1;} }

.nbp-nav-btn {
    width:36px;height:36px;border-radius:10px;border:none;
    display:flex;align-items:center;justify-content:center;
    cursor:pointer;transition:all .2s cubic-bezier(.16,1,.3,1);
    position:relative;flex-shrink:0;
}
.nbp-nav-btn:hover { transform:translateY(-1px); }

.nbp-notif-dropdown { animation:nbpSlideDown .25s cubic-bezier(.16,1,.3,1); }
.nbp-modal-overlay  { animation:nbpFadeIn .2s ease; }
.nbp-modal-dialog   { animation:nbpSlideDown .3s cubic-bezier(.16,1,.3,1); }

.nbp-scroll::-webkit-scrollbar{width:5px;height:5px;}
.nbp-scroll::-webkit-scrollbar-track{background:transparent;}
.nbp-scroll::-webkit-scrollbar-thumb{background:rgba(0,127,200,.25);border-radius:10px;}
.nbp-scroll::-webkit-scrollbar-thumb:hover{background:rgba(0,127,200,.45);}

.nbp-table-row:hover { background:#f8fafc; }
.nbp-action-label{font-size:.72rem;font-weight:800;white-space:nowrap;}
.nbp-nav-btn.nbp-labeled{width:auto;padding:0 10px;gap:6px;}
.nbp-nav-btn:focus-visible,.nbp-user:focus-visible,.nbp-menu-item:focus-visible{outline:3px solid #fbbf24;outline-offset:2px;}

/* Menú de usuario (identidad + Salir) */
.nbp-user-wrap{position:relative;flex-shrink:0;}
.nbp-user{height:36px;min-width:36px;padding:0 8px 0 4px;border-radius:10px;border:none;display:inline-flex;align-items:center;gap:7px;cursor:pointer;transition:all .2s cubic-bezier(.16,1,.3,1);max-width:220px;}
.nbp-user:hover{transform:translateY(-1px);}
.nbp-user-avatar{width:28px;height:28px;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.nbp-user-name{font-size:.76rem;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;}
.nbp-user-chevron{flex-shrink:0;transition:transform .2s;}
.nbp-user[aria-expanded="true"] .nbp-user-chevron{transform:rotate(180deg);}
.nbp-user-menu{position:absolute;top:calc(100% + 8px);right:0;min-width:240px;max-width:calc(100vw - 20px);background:#fff;border:1px solid #e2e8f0;border-radius:12px;box-shadow:0 12px 32px rgba(10,31,68,.18),0 2px 8px rgba(10,31,68,.08);z-index:300;overflow:hidden;animation:nbpSlideDown .2s cubic-bezier(.16,1,.3,1);}
.nbp-menu-identity{padding:12px 14px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;gap:10px;background:#fafcff;}
.nbp-menu-item{width:100%;display:flex;align-items:center;gap:8px;padding:11px 14px;min-height:44px;border:none;background:#fff;color:#0f172a;font-size:.82rem;font-weight:700;cursor:pointer;text-align:left;transition:background .15s;}
.nbp-menu-item:hover{background:#f8fafc;}
.nbp-menu-item.danger{color:#b91c1c;}
.nbp-menu-item.danger:hover{background:#fef2f2;}
@media (max-width: 768px){
  .nbp{height:auto !important;min-height:116px;padding:8px 10px !important;gap:6px;position:sticky !important;top:0;flex-wrap:wrap;}
  .nbp-brand{min-width:0;width:100%;flex:1 0 100%;}
  .nbp-brand>div:last-child{min-width:0;}
  .nbp-brand p:first-child{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .nbp-brand-meta{max-width:calc(100vw - 74px);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .nbp-actions{width:100%;max-width:100%;justify-content:space-between;overflow:visible;padding:2px;gap:4px !important;}
  .nbp-action-label{display:none !important;}
  .nbp-nav-btn,.nbp .nbp-nav-btn.nbp-labeled{width:42px;height:42px;min-width:42px;padding:0;}
  .nbp-user{height:42px;min-width:42px;padding:0 6px;}
  .nbp-user-name{display:none;}
  .nbp-user-menu{right:0;}
  .nbp-modal-overlay{align-items:flex-end !important;padding:0 !important;}
  .nbp-modal-dialog{max-height:92dvh !important;border-radius:22px 22px 0 0 !important;}
}
`;

// ─────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────
interface NavbarProps {
    puntoDeVenta?: {
        nombre: string;
        sucursal: number;
        sucursal_info: { nombre: string; codigo: string; } | null;
    } | null;
    sesionActiva?: { id: number; codigo: string | null; } | null;
    operadorActivo?: OperadorPOSGrant | null;
    onOperatorChange?: (grant: OperadorPOSGrant | null) => void;
}

interface ResumenPagos {
    efectivo: number; banco: number; otro: number;
    total: number; cantidadPagos: number;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const fmtMoneda = (m: number) =>
    new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB', minimumFractionDigits: 2 }).format(m);

const getIconoTipo = (tipo: string) => {
    switch (tipo) {
        case 'efectivo': return <DollarSign size={16} color="#16a34a" />;
        case 'banco': return <Card size={16} color={C.secondary} />;
        case 'qr': return <QrCode size={16} color="#7c3aed" />;
        default: return <Wallet size={16} color="#64748b" />;
    }
};

const getNombreTipo = (tipo: string) => ({ efectivo: 'Efectivo', banco: 'Banco/Tarjeta', qr: 'QR' }[tipo] || 'Otro');

// ─────────────────────────────────────────────
// Modal wrapper — siempre sobre todo
// ─────────────────────────────────────────────
const PosModal = ({ onClose, children, titleId, maxWidth = '480px' }: { onClose: () => void; children: React.ReactNode; titleId: string; maxWidth?: string }) =>
    createPortal(
        <div className="nbp-modal-overlay" onMouseDown={event=>{if(event.target===event.currentTarget)onClose();}} style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(6px)' }}>
            <div role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onClick={e => e.stopPropagation()} className="nbp-modal-dialog" style={{ background: '#fff', borderRadius: '22px', width: '100%', maxWidth, boxShadow: '0 32px 80px rgba(0,0,0,.2)', border: '1.5px solid #f1f5f9', overflow: 'hidden' }}>
                <div style={{ height: '4px', background: `linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})` }} />
                {children}
            </div>
        </div>,
        document.body
    );

// ─────────────────────────────────────────────
// Identidad operativa del navbar
// ─────────────────────────────────────────────
const actorIdentityOf = (user: any): string | null => (
    user ? String(user.id ?? user.email ?? `${user.first_name}|${user.last_name}`) : null
);
const pointIdentityOf = (puntoDeVenta: NavbarProps['puntoDeVenta']): string | null => (
    puntoDeVenta
        ? `${(puntoDeVenta as any).id ?? ''}|${puntoDeVenta.sucursal}|${puntoDeVenta.nombre}|${puntoDeVenta.sucursal_info?.codigo ?? ''}|${puntoDeVenta.sucursal_info?.nombre ?? ''}`
        : null
);

// ─────────────────────────────────────────────
// NAVBAR PRINCIPAL (partición por identidad)
// ─────────────────────────────────────────────
/**
 * El navbar se monta con ``key`` = actor + punto de venta + sucursal + sesión
 * exacta. Cualquier cambio en esa identidad desmonta el árbol anterior
 * completo (modales de caja/pagos/salida, resúmenes, totales, menú) y monta
 * uno limpio: ningún modal, resumen ni pago de la identidad A se renderiza
 * (ni un frame) ni es accionable bajo la identidad B. Las continuaciones
 * asíncronas de A quedan descartadas por los guards de montaje/request-id.
 */
const NavbarPos: React.FC<NavbarProps> = (props) => {
    const { user } = useAuth();
    const identity = `${actorIdentityOf(user) ?? ''}#${pointIdentityOf(props.puntoDeVenta) ?? ''}#${props.sesionActiva?.id ?? ''}#${props.operadorActivo?.operador.id ?? ''}`;
    return <NavbarPosView key={identity} {...props} />;
};

const NavbarPosView: React.FC<NavbarProps> = ({ puntoDeVenta, sesionActiva, operadorActivo, onOperatorChange }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Estados para caja, pagos y logout (sin notificaciones)
    const [showCajaModal, setShowCajaModal] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [sesionActivaLocal, setSesionActivaLocal] = useState<SesionActivaResponse | null>(null);
    const [loadingSesion, setLoadingSesion] = useState(false);
    const [showPagosModal, setShowPagosModal] = useState(false);
    const [pagosSesion, setPagosSesion] = useState<Pago[]>([]);
    const [loadingPagos, setLoadingPagos] = useState(false);
    const [resumenPagos, setResumenPagos] = useState<ResumenPagos>({ efectivo: 0, banco: 0, otro: 0, total: 0, cantidadPagos: 0 });
    const [totalVentas, setTotalVentas] = useState(0);
    const [loadingVentas, setLoadingVentas] = useState(false);
    const [showTodayOccupancy, setShowTodayOccupancy] = useState(false);
    const [showRequestCode, setShowRequestCode] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [showOperatorModal, setShowOperatorModal] = useState(false);
    const [operators, setOperators] = useState<OperadorPOS[]>([]);
    const [selectedOperator, setSelectedOperator] = useState<OperadorPOS | null>(null);
    const [operatorPin, setOperatorPin] = useState('');
    const [operatorLoading, setOperatorLoading] = useState(false);
    const [operatorError, setOperatorError] = useState('');
    const userMenuWrapRef = useRef<HTMLDivElement | null>(null);
    const userTriggerRef = useRef<HTMLButtonElement | null>(null);
    const cajaRequestRef = useRef(0);
    const ventasRequestRef = useRef(0);
    const pagosRequestRef = useRef(0);
    const interactionRequestRef = useRef(0);
    // Modal de operador: listar y activar comparten UNA generación. Abrir,
    // cerrar (botón, overlay, Escape) o desmontar la incrementan: ninguna
    // respuesta tardía puede pisar operadores/errores/carga ni instalar un grant.
    const operatorRequestRef = useRef(0);
    // Toda continuación asíncrona (efectos y handlers, incluido logout/navegación)
    // exige que el navbar siga montado Y que su request-id siga vigente.
    const mountedRef = useRef(true);
    const isLive = (ref: React.MutableRefObject<number>, requestId: number) =>
        mountedRef.current && ref.current === requestId;

    useEffect(() => {
        mountedRef.current = true;
        // Contadores (no nodos DOM): capturarlos aquí es exactamente lo que
        // queremos invalidar en el cleanup de desmontaje.
        const requestCounters = [cajaRequestRef, ventasRequestRef, pagosRequestRef, interactionRequestRef, operatorRequestRef];
        return () => {
            mountedRef.current = false;
            requestCounters.forEach(counter => { counter.current += 1; });
        };
    }, []);
    // La identidad operativa es fija durante la vida de esta vista: el wrapper
    // ``NavbarPos`` la usa como ``key`` y remonta la vista al cambiar, así que
    // ningún estado (modales, resúmenes, pagos) puede sobrevivir a un cambio
    // de actor, punto, sucursal o sesión.
    const actorIdentity = actorIdentityOf(user);
    const pointIdentity = pointIdentityOf(puntoDeVenta);
    const hasOperationalIdentity = actorIdentity !== null && pointIdentity !== null;

    useEffect(() => {
        if (!showCajaModal && !showPagosModal && !showLogoutModal && !showOperatorModal) return;
        const previousFocus = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const dialog = document.querySelector<HTMLElement>('.nbp-modal-dialog[role="dialog"]');
        const focusableSelector = 'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
        window.setTimeout(() => (dialog?.querySelector<HTMLElement>(focusableSelector) || dialog)?.focus(), 0);
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShowCajaModal(false); setShowPagosModal(false); setShowLogoutModal(false); closeOperatorSelector();
                return;
            }
            if (event.key !== 'Tab' || !dialog) return;
            const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector));
            if (!focusable.length) { event.preventDefault(); dialog.focus(); return; }
            const first = focusable[0], last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
            if (previousFocus?.isConnected) previousFocus.focus();
        };
    }, [showCajaModal, showPagosModal, showLogoutModal, showOperatorModal]);

    // Menú de usuario: cierre por click fuera y Escape (devuelve el foco al disparador)
    useEffect(() => {
        if (!userMenuOpen) return;
        const onPointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (target && userMenuWrapRef.current?.contains(target)) return;
            setUserMenuOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            setUserMenuOpen(false);
            userTriggerRef.current?.focus();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [userMenuOpen]);

    useEffect(() => {
        if (!userMenuOpen) return;
        const first = userMenuWrapRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
        window.setTimeout(() => first?.focus(), 0);
    }, [userMenuOpen]);

    // Sesión para el modal de caja: SIEMPRE la sesión mostrada, revalidada por id.
    const sesionMostradaId = sesionActiva?.id ?? null;
    useEffect(() => {
        const requestId = ++cajaRequestRef.current;
        if (!showCajaModal || !hasOperationalIdentity || sesionMostradaId === null) { setLoadingSesion(false); return; }
        setLoadingSesion(true);
        obtenerSesionActivaPorId(sesionMostradaId)
            .then((value: SesionActivaResponse) => {
                if (!isLive(cajaRequestRef, requestId)) return;
                setSesionActivaLocal(value && value.id === sesionMostradaId ? value : null);
            })
            .catch((e: unknown) => { if (isLive(cajaRequestRef, requestId)) { console.error(e); setSesionActivaLocal(null); } })
            .finally(() => { if (isLive(cajaRequestRef, requestId)) setLoadingSesion(false); });
    }, [showCajaModal, sesionMostradaId, actorIdentity, pointIdentity, hasOperationalIdentity]);

    // Total ventas
    useEffect(() => {
        const requestId = ++ventasRequestRef.current;
        if (!hasOperationalIdentity || !sesionActiva?.id) { setTotalVentas(0); setLoadingVentas(false); return; }
        setLoadingVentas(true);
        fetchPagosPorSesionPOS(sesionActiva.id)
            .then((p: Pago[]) => { if (isLive(ventasRequestRef, requestId)) setTotalVentas(p.reduce((s: number, x: Pago) => s + parseFloat(x.monto.toString()), 0)); })
            .catch(() => { if (isLive(ventasRequestRef, requestId)) setTotalVentas(0); })
            .finally(() => { if (isLive(ventasRequestRef, requestId)) setLoadingVentas(false); });
    }, [sesionActiva?.id, actorIdentity, pointIdentity, hasOperationalIdentity]);

    // Pagos sesión
    useEffect(() => {
        const requestId = ++pagosRequestRef.current;
        if (!showPagosModal || !hasOperationalIdentity || !sesionActiva?.id) {
            setPagosSesion([]);
            setResumenPagos({ efectivo: 0, banco: 0, otro: 0, total: 0, cantidadPagos: 0 });
            setLoadingPagos(false);
            return;
        }
        setLoadingPagos(true);
        fetchPagosPorSesionPOS(sesionActiva.id).then(pagos => {
            if (!isLive(pagosRequestRef, requestId)) return;
            setPagosSesion(pagos);
            const r: ResumenPagos = { efectivo: 0, banco: 0, otro: 0, total: 0, cantidadPagos: pagos.length };
            pagos.forEach(p => { const t = p.tipo_pago_configuracion?.tipo || 'otro', m = parseFloat(p.monto.toString()); if (t === 'efectivo') r.efectivo += m; else if (t === 'banco') r.banco += m; else r.otro += m; r.total += m; });
            setResumenPagos(r); setTotalVentas(r.total);
        }).catch(() => { if (isLive(pagosRequestRef, requestId)) setPagosSesion([]); })
            .finally(() => { if (isLive(pagosRequestRef, requestId)) setLoadingPagos(false); });
    }, [showPagosModal, sesionActiva?.id, actorIdentity, pointIdentity, hasOperationalIdentity]);

    // Revalida por id la sesión MOSTRADA. Resultado:
    //  'ok'      -> propia, abierta, punto activo/asignado y sucursal activa;
    //  'closed'  -> el servidor la rechazó (404/409): ya no es operable, aunque
    //               el operador tenga otra caja abierta (nunca se cambia a ella);
    //  'error'   -> fallo de red/servidor.
    const revalidarSesionMostrada = async (): Promise<'ok' | 'closed' | 'error'> => {
        if (sesionMostradaId === null) return 'closed';
        try {
            const vigente = await obtenerSesionActivaPorId(sesionMostradaId);
            return vigente && vigente.id === sesionMostradaId ? 'ok' : 'closed';
        } catch (error: any) {
            const status = error?.response?.status;
            return status === 404 || status === 409 ? 'closed' : 'error';
        }
    };
    const SESION_NO_OPERABLE = 'La sesión POS mostrada ya no está abierta o no es operable. Vuelve al listado de POS para continuar.';
    const handleCajaClick = async () => {
        const requestId = ++interactionRequestRef.current;
        if (!hasOperationalIdentity) return;
        const estado = await revalidarSesionMostrada();
        if (!isLive(interactionRequestRef, requestId)) return;
        if (estado === 'ok') setShowCajaModal(true);
        else if (estado === 'closed') alert(SESION_NO_OPERABLE);
        else alert('Error verificando sesión.');
    };
    const handlePagosClick = async () => {
        const requestId = ++interactionRequestRef.current;
        if (!hasOperationalIdentity) return;
        const estado = await revalidarSesionMostrada();
        if (!isLive(interactionRequestRef, requestId)) return;
        if (estado === 'ok') setShowPagosModal(true);
        else if (estado === 'closed') alert(SESION_NO_OPERABLE);
        else alert('Error verificando sesión.');
    };
    const handleLogout = async (requestId: number) => {
        // ``logout`` ya redirige a /login por su cuenta; la navegación local solo
        // se ejecuta si este navbar sigue montado y la interacción sigue vigente.
        if (logout) await logout();
        if (!isLive(interactionRequestRef, requestId)) return;
        navigate('/login');
    };
    const handleSalirClick = async () => {
        const requestId = ++interactionRequestRef.current;
        setUserMenuOpen(false);
        if (!hasOperationalIdentity) return;
        const estado = await revalidarSesionMostrada();
        if (!isLive(interactionRequestRef, requestId)) return;
        if (estado === 'ok') setShowLogoutModal(true);
        else if (estado === 'closed') await handleLogout(requestId);
        else console.error('Error verificando sesión.');
    };
    const fmtCodigo = () => sesionActiva?.codigo || (sesionActiva?.id ? `#${sesionActiva.id}` : '—');
    const userFullName = user ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() : '';
    const userLabel = userFullName || (user as any)?.email || 'Operador';
    const userRole = (user as any)?.tipo_nivel_usuario?.nombre || (user as any)?.rol || null;
    const onUserTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setUserMenuOpen(true);
        }
    };
    const onUserMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') return;
        const items = Array.from(userMenuWrapRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []);
        if (!items.length) return;
        event.preventDefault();
        const index = items.indexOf(document.activeElement as HTMLElement);
        const next = event.key === 'Home' ? 0
            : event.key === 'End' ? items.length - 1
            : event.key === 'ArrowDown' ? (index + 1) % items.length
            : (index - 1 + items.length) % items.length;
        items[next].focus();
    };

    const isPOS = !!(puntoDeVenta && puntoDeVenta.sucursal_info);

    const closeOperatorSelector = () => {
        operatorRequestRef.current += 1;
        setShowOperatorModal(false);
        setOperatorLoading(false);
        setOperatorError('');
        setOperatorPin('');
    };

    const openOperatorSelector = async () => {
        if (!sesionActiva?.id) return;
        const requestId = ++operatorRequestRef.current;
        const requestedSessionId = sesionActiva.id;
        setShowOperatorModal(true);
        setOperators([]);
        setSelectedOperator(null);
        setOperatorPin('');
        setOperatorError('');
        setOperatorLoading(true);
        try {
            const list = await fetchOperadoresPOS(requestedSessionId);
            if (!isLive(operatorRequestRef, requestId)) return;
            setOperators(list);
        } catch (error) {
            if (!isLive(operatorRequestRef, requestId)) return;
            console.error(error);
            setOperators([]);
            setOperatorError('No se pudieron cargar los operadores autorizados.');
        } finally {
            if (isLive(operatorRequestRef, requestId)) setOperatorLoading(false);
        }
    };

    const submitOperatorPin = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!sesionActiva?.id || !selectedOperator || !/^\d{3,6}$/.test(operatorPin)) {
            setOperatorError('Ingresa un PIN numérico de 3 a 6 dígitos.');
            return;
        }
        const requestId = ++operatorRequestRef.current;
        const requestedSessionId = sesionActiva.id;
        setOperatorLoading(true);
        setOperatorError('');
        try {
            const grant = await activarOperadorPOS(requestedSessionId, selectedOperator.id, operatorPin);
            // Solo se instala el grant si el navbar sigue montado, el modal sigue
            // siendo esta misma apertura y la sesión mostrada no cambió.
            if (!isLive(operatorRequestRef, requestId) || sesionActiva?.id !== requestedSessionId) return;
            onOperatorChange?.(grant);
            closeOperatorSelector();
        } catch (error: any) {
            if (!isLive(operatorRequestRef, requestId)) return;
            setOperatorError(error?.response?.data?.detail || error?.response?.data?.error || 'PIN incorrecto o acceso no autorizado.');
        } finally {
            if (isLive(operatorRequestRef, requestId)) setOperatorLoading(false);
        }
    };

    // Colores del navbar según modo
    const navBg = isPOS ? `linear-gradient(135deg,${C.secondaryDark},${C.secondary})` : '#fff';
    const btnBg = isPOS ? 'rgba(255,255,255,.15)' : `${C.secondary}0d`;
    const btnHover = isPOS ? 'rgba(255,255,255,.25)' : `${C.secondary}1a`;
    const iconColor = isPOS ? '#fff' : C.secondary;
    const textColor = isPOS ? '#fff' : '#0f172a';

    return (
        <>
            <style>{css}</style>

            {/* ─── NAVBAR ─── */}
            <nav className="nbp" aria-label="Navegación del punto de venta" style={{
                width: '100%', height: '60px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', padding: '0 20px',
                background: navBg, flexShrink: 0,
                boxShadow: isPOS ? '0 4px 20px rgba(0,127,200,.3)' : '0 1px 0 #f1f5f9',
                position: 'relative', zIndex: 100,
            }}>
                {/* Barra top de colores — solo en modo no-POS */}
                {!isPOS && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})` }} />}

                {/* ── LADO IZQUIERDO ── */}
                {isPOS ? (
                    <div className="nbp-brand" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Building size={18} color="#fff" />
                        </div>
                        <div>
                            <p style={{ fontSize: '.95rem', fontWeight: '900', color: '#fff', lineHeight: 1.1, letterSpacing: '-.01em' }}>{puntoDeVenta!.sucursal_info!.nombre}</p>
                            <p className="nbp-brand-meta" style={{ fontSize: '.68rem', color: 'rgba(255,255,255,.75)', fontWeight: '500', marginTop: '2px' }}>
                                {puntoDeVenta!.nombre} · {puntoDeVenta!.sucursal_info!.codigo} · Sesión: <span style={{ fontWeight: '700', color: 'rgba(255,255,255,.95)' }}>{fmtCodigo()}</span>
                            </p>
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: `${C.secondary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Building size={16} color={C.secondary} />
                        </div>
                        <div>
                            <p style={{ fontSize: '1rem', fontWeight: '900', color: '#0f172a', letterSpacing: '-.02em', lineHeight: 1 }}>
                                <span style={{ color: C.secondary }}>Co</span>Marca <span style={{ fontWeight: '400', color: '#64748b', fontSize: '.85rem' }}>Cowork</span>
                            </p>
                            <div style={{ display: 'flex', gap: '4px', marginTop: '3px' }}>
                                {[C.primary, C.secondary, C.accentDark, C.secondaryLight].map((c, i) => <div key={i} style={{ height: '2px', width: i === 0 ? '14px' : '5px', borderRadius: '2px', background: c }} />)}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── LADO DERECHO ── */}
                <div className="nbp-actions" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                    {/* Total ventas */}
                    <div style={{ position: 'relative' }} className="group">
                        <button className="nbp-nav-btn nbp-labeled" aria-label="Pagos de la sesión" onClick={handlePagosClick} title="Pagos de la sesión"
                            style={{ background: btnBg }}
                            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = btnHover}
                            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = btnBg}>
                            <Banknote size={17} color={iconColor} /><span className="nbp-action-label" style={{color:iconColor}}>Pagos</span>
                        </button>
                        {/* Tooltip */}
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', background: '#0f172a', color: '#fff', fontSize: '.68rem', fontWeight: '700', padding: '4px 10px', borderRadius: '7px', boxShadow: '0 4px 14px rgba(0,0,0,.2)', whiteSpace: 'nowrap', pointerEvents: 'none', opacity: 0, transition: 'opacity .15s' }}
                            className="nbp-tooltip">
                            {loadingVentas ? 'Cargando...' : fmtMoneda(totalVentas)}
                        </div>
                    </div>

                    {/* Agenda de ocupación de hoy */}
                    <div style={{ position: 'relative' }} className="group">
                        <button
                            className="nbp-nav-btn nbp-labeled"
                            aria-label="Ver espacios ocupados hoy"
                            title="Espacios ocupados hoy"
                            onClick={() => setShowTodayOccupancy(true)}
                            style={{ background: btnBg }}
                            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = btnHover}
                            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = btnBg}
                        >
                            <CalendarClock size={17} color={iconColor} /><span className="nbp-action-label" style={{color:iconColor}}>Agenda</span>
                        </button>
                        <div className="nbp-tooltip" style={{ position: 'absolute', top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', background: '#0f172a', color: '#fff', fontSize: '.68rem', fontWeight: '700', padding: '4px 10px', borderRadius: '7px', boxShadow: '0 4px 14px rgba(0,0,0,.2)', whiteSpace: 'nowrap', pointerEvents: 'none', opacity: 0, transition: 'opacity .15s' }}>
                            Ocupación de hoy
                        </div>
                    </div>

                    {/* Registro directo de solicitudes web en la caja mostrada */}
                    <button
                        type="button"
                        className="nbp-nav-btn nbp-labeled"
                        aria-label="Registrar código de reserva"
                        title="Registrar código de reserva"
                        onClick={() => setShowRequestCode(true)}
                        style={{ background: btnBg }}
                        onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = btnHover}
                        onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = btnBg}
                    >
                        <KeyRound size={17} color={iconColor} />
                        <span className="nbp-action-label" style={{color:iconColor}}>Código</span>
                    </button>

                    {/* Notificaciones - Componente independiente */}
                    <NotificacionesPos
                        isPOS={isPOS} btnBg={btnBg} btnHover={btnHover} iconColor={iconColor}
                        sesionPosId={sesionActiva?.id ?? null}
                        operadorToken={operadorActivo?.token ?? null}
                    />

                    {/* Caja */}
                    <button className="nbp-nav-btn nbp-labeled" aria-label="Movimientos de caja" onClick={handleCajaClick} title="Movimientos de Caja"
                        style={{ background: btnBg }}
                        onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = btnHover}
                        onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = btnBg}>
                        <PiggyBank size={17} color={iconColor} /><span className="nbp-action-label" style={{color:iconColor}}>Caja</span>
                    </button>

                    {/* Operador del turno: identidad auditada por PIN, separada del usuario autenticado. */}
                    <button
                        type="button"
                        className="nbp-user"
                        aria-label={operadorActivo ? `Cambiar operador de caja: ${operadorActivo.operador.nombre}` : 'Seleccionar operador de caja'}
                        title={operadorActivo ? `Operador: ${operadorActivo.operador.nombre}` : 'Identificar operador'}
                        onClick={openOperatorSelector}
                        style={{ background: operadorActivo ? 'rgba(0,219,185,.24)' : btnBg }}
                    >
                        <span className="nbp-user-avatar" aria-hidden="true" style={{ background: 'rgba(255,255,255,.2)' }}>
                            <KeyRound size={14} color={iconColor} />
                        </span>
                        <span className="nbp-user-name" style={{ color: textColor }}>
                            {operadorActivo?.operador.nombre || 'Operador'}
                        </span>
                    </button>

                    {/* Usuario: identidad + Salir en un único menú */}
                    <div className="nbp-user-wrap" ref={userMenuWrapRef}>
                        <button
                            ref={userTriggerRef}
                            type="button"
                            className="nbp-user"
                            id="nbp-user-trigger"
                            aria-haspopup="menu"
                            aria-expanded={userMenuOpen}
                            aria-controls={userMenuOpen ? 'nbp-user-menu' : undefined}
                            aria-label={`Menú de usuario: ${userLabel}`}
                            title={userLabel}
                            onClick={() => setUserMenuOpen(open => !open)}
                            onKeyDown={onUserTriggerKeyDown}
                            style={{ background: btnBg }}
                            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = btnHover}
                            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = btnBg}
                        >
                            <span className="nbp-user-avatar" aria-hidden="true" style={{ background: isPOS ? 'rgba(255,255,255,.2)' : `${C.secondary}12` }}>
                                <User size={14} color={iconColor} />
                            </span>
                            <span className="nbp-user-name" style={{ color: textColor }}>{userLabel}</span>
                            <ChevronDown size={13} color={iconColor} className="nbp-user-chevron" aria-hidden="true" />
                        </button>
                        {userMenuOpen && (
                            <div
                                id="nbp-user-menu"
                                role="menu"
                                aria-labelledby="nbp-user-trigger"
                                className="nbp-user-menu"
                                onKeyDown={onUserMenuKeyDown}
                            >
                                <div className="nbp-menu-identity">
                                    <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: `${C.secondary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <User size={16} color={C.secondary} />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <p style={{ fontSize: '.84rem', fontWeight: '800', color: '#0f172a', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userLabel}</p>
                                        {(user as any)?.email && <p style={{ fontSize: '.7rem', color: '#64748b', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(user as any).email}</p>}
                                        <p style={{ fontSize: '.66rem', color: '#94a3b8', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {userRole ? `${userRole} · ` : ''}{isPOS ? `${puntoDeVenta!.nombre} · Sesión ${fmtCodigo()}` : 'Punto de venta general'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="nbp-menu-item danger"
                                    aria-label="Salir del punto de venta"
                                    onClick={handleSalirClick}
                                >
                                    <LogOut size={15} /> Salir
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            {showOperatorModal && (
                <PosModal onClose={closeOperatorSelector} titleId="nbp-operator-title" maxWidth="520px">
                    <form onSubmit={submitOperatorPin} style={{ padding: '22px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '16px' }}>
                            <div>
                                <h3 id="nbp-operator-title" style={{ fontSize: '1.08rem', fontWeight: 900, color: '#0f172a', margin: 0 }}>Identificar operador de caja</h3>
                                <p style={{ fontSize: '.76rem', color: '#64748b', marginTop: '4px' }}>Elige tu nombre e ingresa tu PIN personal.</p>
                            </div>
                            <button type="button" aria-label="Cerrar identificación de operador" onClick={closeOperatorSelector} style={{ width: '32px', height: '32px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', display: 'grid', placeItems: 'center', cursor: 'pointer' }}><X size={15}/></button>
                        </div>
                        {operatorLoading && operators.length === 0 ? <p role="status">Cargando operadores…</p> : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '10px', marginBottom: '16px' }}>
                                {operators.map(operator => (
                                    <button
                                        key={operator.id}
                                        type="button"
                                        disabled={!operator.pin_configurado}
                                        aria-label={operator.nombre}
                                        onClick={() => { setSelectedOperator(operator); setOperatorPin(''); setOperatorError(''); }}
                                        style={{ padding: '13px', borderRadius: '12px', border: `2px solid ${selectedOperator?.id === operator.id ? C.secondary : '#e2e8f0'}`, background: selectedOperator?.id === operator.id ? '#eef8fd' : '#fff', color: '#0f172a', fontWeight: 800, cursor: operator.pin_configurado ? 'pointer' : 'not-allowed', opacity: operator.pin_configurado ? 1 : .55 }}
                                    >
                                        {operator.nombre}
                                        {!operator.pin_configurado && <span style={{ display: 'block', fontSize: '.66rem', color: '#94a3b8', marginTop: '3px' }}>PIN no configurado</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                        {selectedOperator && (
                            <div>
                                <label htmlFor="nbp-operator-pin" style={{ display: 'block', fontSize: '.78rem', fontWeight: 800, color: '#334155', marginBottom: '6px' }}>PIN de caja</label>
                                <input id="nbp-operator-pin" type="password" inputMode="numeric" autoComplete="off" pattern="[0-9]{3,6}" minLength={3} maxLength={6} value={operatorPin} onChange={event => setOperatorPin(event.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus style={{ width: '100%', padding: '12px 14px', borderRadius: '11px', border: '1.5px solid #cbd5e1', fontSize: '1.1rem', letterSpacing: '.3em' }}/>
                            </div>
                        )}
                        {operatorError && <p role="alert" style={{ color: '#b91c1c', fontSize: '.75rem', fontWeight: 700, marginTop: '10px' }}>{operatorError}</p>}
                        {selectedOperator && <button type="submit" disabled={operatorLoading || !/^\d{3,6}$/.test(operatorPin)} style={{ width: '100%', marginTop: '16px', padding: '11px', border: 0, borderRadius: '11px', background: C.secondary, color: '#fff', fontWeight: 900, cursor: 'pointer', opacity: operatorLoading ? .65 : 1 }}>Ingresar como {selectedOperator.nombre}</button>}
                    </form>
                </PosModal>
            )}

            <SolicitudReservaPOSModal
                open={showRequestCode}
                onClose={() => setShowRequestCode(false)}
                onOfficialized={() => setShowRequestCode(false)}
                preferredSessionId={sesionActiva?.id ?? null}
                operadorToken={operadorActivo?.token ?? null}
            />

            {puntoDeVenta?.sucursal && (
                <TodayOccupiedSpacesModal
                    open={showTodayOccupancy}
                    onClose={() => setShowTodayOccupancy(false)}
                    sucursalId={puntoDeVenta.sucursal}
                />
            )}

            {/* ──────────────────────────────────────────────────────────
                MODALES — todos via createPortal para evitar z-index issues
            ────────────────────────────────────────────────────────── */}

            {/* Modal caja */}
            {showCajaModal && createPortal(
                <div className="nbp-modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(6px)' }}>
                    <div className="nbp-modal-dialog nbp-scroll" role="dialog" aria-modal="true" aria-labelledby="nbp-caja-title" tabIndex={-1} onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '22px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,.2)', border: '1.5px solid #f1f5f9' }}>
                        <div style={{ height: '4px', background: `linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})` }} />
                        <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '11px', background: `${C.secondary}10`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <PiggyBank size={18} color={C.secondary} />
                                </div>
                                <div>
                                    <h3 id="nbp-caja-title" style={{ fontSize: '1.05rem', fontWeight: '900', color: '#0f172a', letterSpacing: '-.02em' }}>Movimientos de Caja</h3>
                                    <p style={{ fontSize: '.72rem', color: '#94a3b8' }}>Sesión: <strong>{fmtCodigo()}</strong></p>
                                </div>
                            </div>
                            <button aria-label="Cerrar movimientos de caja" onClick={() => setShowCajaModal(false)} style={{ width: '32px', height: '32px', borderRadius: '10px', border: '1.5px solid #f1f5f9', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                <X size={15} color="#64748b" />
                            </button>
                        </div>
                        <div style={{ padding: '16px 24px 24px' }}>
                            {loadingSesion ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', gap: '12px', alignItems: 'center' }}>
                                    <div style={{ width: '32px', height: '32px', border: `3px solid ${C.secondary}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'nbpSpin .7s linear infinite' }} />
                                    <p style={{ color: '#64748b', fontWeight: '600', fontSize: '.85rem' }}>Cargando sesión...</p>
                                </div>
                            ) : sesionActivaLocal ? (
                                <GestionMovimientosCaja sesionId={sesionActivaLocal.id} saldoInicial={sesionActivaLocal.saldo_inicial} operadorToken={operadorActivo?.token ?? null} />
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', background: '#f8fafc', borderRadius: '14px', border: '1.5px dashed #e2e8f0' }}>
                                    <p style={{ color: '#94a3b8', fontWeight: '600' }}>No se pudo cargar la sesión activa</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal pagos sesión */}
            {showPagosModal && createPortal(
                <div className="nbp-modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(6px)' }}>
                    {/* Diálogo sin overflow propio, usará flex column */}
                    <div className="nbp-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="nbp-pagos-title" tabIndex={-1} onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '22px', width: '100%', maxWidth: '860px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,.2)', border: '1.5px solid #f1f5f9', overflow: 'hidden' }}>
                        <div style={{ height: '4px', background: `linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})` }} />

                        {/* Encabezado fijo */}
                        <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '11px', background: `${C.secondary}10`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Banknote size={18} color={C.secondary} />
                                </div>
                                <div>
                                    <h3 id="nbp-pagos-title" style={{ fontSize: '1.05rem', fontWeight: '900', color: '#0f172a', letterSpacing: '-.02em' }}>Pagos de la Sesión</h3>
                                    <p style={{ fontSize: '.72rem', color: '#94a3b8' }}>Sesión: <strong>{fmtCodigo()}</strong></p>
                                </div>
                            </div>
                            <button aria-label="Cerrar pagos de la sesión" onClick={() => setShowPagosModal(false)} style={{ width: '32px', height: '32px', borderRadius: '10px', border: '1.5px solid #f1f5f9', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                <X size={15} color="#64748b" />
                            </button>
                        </div>

                        {/* Área con scroll vertical personalizado */}
                        <div className="nbp-scroll" style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
                            {loadingPagos ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', gap: '12px', alignItems: 'center' }}>
                                    <div style={{ width: '32px', height: '32px', border: `3px solid ${C.secondary}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'nbpSpin .7s linear infinite' }} />
                                    <p style={{ color: '#64748b', fontWeight: '600', fontSize: '.85rem' }}>Cargando pagos...</p>
                                </div>
                            ) : pagosSesion.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', background: '#f8fafc', borderRadius: '14px', border: '1.5px dashed #e2e8f0' }}>
                                    <Banknote size={32} color="#d1d5db" style={{ margin: '0 auto 10px' }} />
                                    <p style={{ color: '#94a3b8', fontWeight: '600' }}>Sin pagos registrados en esta sesión</p>
                                </div>
                            ) : (
                                <>
                                    {/* Resumen 4 cards */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '12px', marginBottom: '20px' }}>
                                        {[
                                            { label: 'Efectivo', value: resumenPagos.efectivo, count: pagosSesion.filter(p => p.tipo_pago_configuracion?.tipo === 'efectivo').length, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: <DollarSign size={18} color="#16a34a" /> },
                                            { label: 'Banco/Tarjeta', value: resumenPagos.banco, count: pagosSesion.filter(p => p.tipo_pago_configuracion?.tipo === 'banco').length, color: C.secondary, bg: `${C.secondary}08`, border: `${C.secondary}25`, icon: <Card size={18} color={C.secondary} /> },
                                            { label: 'Otros', value: resumenPagos.otro, count: pagosSesion.filter(p => !['efectivo', 'banco'].includes(p.tipo_pago_configuracion?.tipo || '')).length, color: '#7c3aed', bg: '#faf5ff', border: '#e9d5ff', icon: <Wallet size={18} color="#7c3aed" /> },
                                            { label: 'Total', value: resumenPagos.total, count: resumenPagos.cantidadPagos, color: C.primary, bg: `${C.primary}08`, border: `${C.primary}22`, icon: <Banknote size={18} color={C.primary} /> },
                                        ].map(s => (
                                            <div key={s.label} style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: '14px', padding: '14px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                    {s.icon}
                                                    <span style={{ fontSize: '1rem', fontWeight: '900', color: s.color }}>{fmtMoneda(s.value)}</span>
                                                </div>
                                                <p style={{ fontSize: '.78rem', fontWeight: '700', color: s.color }}>{s.label}</p>
                                                <p style={{ fontSize: '.68rem', color: '#94a3b8', marginTop: '2px' }}>{s.count} pago{s.count !== 1 ? 's' : ''}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Tabla con scroll horizontal personalizado */}
                                    <div className="nbp-scroll" style={{ overflowX: 'auto', borderRadius: '14px', border: '1.5px solid #f1f5f9' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'DM Sans',sans-serif", minWidth: '620px' }}>
                                            <thead>
                                                <tr style={{ background: '#fafcff', borderBottom: '1.5px solid #f1f5f9' }}>
                                                    {['Código', 'Tipo', 'Monto', 'Ef. Recibido', 'Cambio', 'Fecha'].map(h => (
                                                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '.65rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.08em', whiteSpace: 'nowrap' }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pagosSesion.map(p => (
                                                    <tr key={p.id} className="nbp-table-row" style={{ borderBottom: '1px solid #f8fafc', transition: 'background .15s' }}>
                                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                                                            <span style={{ background: `${C.secondary}10`, color: C.secondary, borderRadius: '6px', padding: '2px 8px', fontSize: '.7rem', fontWeight: '700' }}>{p.codigo || '—'}</span>
                                                        </td>
                                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                {getIconoTipo(p.tipo_pago_configuracion?.tipo || 'otro')}
                                                                <span style={{ fontSize: '.82rem', fontWeight: '600', color: '#374151' }}>{p.tipo_pago_nombre || getNombreTipo(p.tipo_pago_configuracion?.tipo || 'otro')}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                                                            <span style={{ fontSize: '.88rem', fontWeight: '800', color: '#0f172a' }}>{fmtMoneda(parseFloat(p.monto.toString()))}</span>
                                                        </td>
                                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', fontSize: '.82rem', color: '#64748b' }}>{p.efectivo_recibido ? fmtMoneda(parseFloat(p.efectivo_recibido.toString())) : '—'}</td>
                                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', fontSize: '.82rem', color: '#64748b' }}>{p.cambio ? fmtMoneda(parseFloat(p.cambio.toString())) : '—'}</td>
                                                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', fontSize: '.75rem', color: '#94a3b8' }}>
                                                            {new Date(p.fecha_pago).toLocaleDateString()} {new Date(p.fecha_pago).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Banner total */}
                                    <div style={{ marginTop: '16px', padding: '14px 18px', borderRadius: '14px', background: `linear-gradient(135deg,${C.secondary},${C.secondaryDark})`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <p style={{ fontSize: '.82rem', fontWeight: '700', color: 'rgba(255,255,255,.8)' }}>Total Recaudado</p>
                                            <p style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.6)' }}>{resumenPagos.cantidadPagos} pagos · Sesión {fmtCodigo()}</p>
                                        </div>
                                        <p style={{ fontSize: '1.4rem', fontWeight: '900', color: '#fff' }}>{fmtMoneda(resumenPagos.total)}</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal logout */}
            {showLogoutModal && (
                <PosModal onClose={() => setShowLogoutModal(false)} titleId="nbp-logout-title" maxWidth="420px">
                    <div style={{ padding: '20px 22px 22px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '11px', background: `${C.accentDark}10`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <AlertCircle size={18} color={C.accentDark} />
                                </div>
                                <div>
                                    <h3 id="nbp-logout-title" style={{ fontSize: '1rem', fontWeight: '900', color: '#0f172a', letterSpacing: '-.02em' }}>Cierre de Sesión POS</h3>
                                    <p style={{ fontSize: '.72rem', color: '#94a3b8' }}>Hay una sesión activa</p>
                                </div>
                            </div>
                            <button aria-label="Cerrar confirmación de salida" onClick={() => setShowLogoutModal(false)} style={{ width: '30px', height: '30px', borderRadius: '9px', border: '1.5px solid #f1f5f9', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                <X size={14} color="#64748b" />
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '14px' }}>
                            {[C.primary, C.secondary, C.accentDark, C.secondaryLight].map((c, i) => <div key={i} style={{ height: '2px', width: i === 0 ? '16px' : '5px', borderRadius: '2px', background: c, opacity: .5 }} />)}
                        </div>
                        <div style={{ padding: '12px 14px', background: `${C.accentDark}08`, border: `1.5px solid ${C.accentDark}20`, borderRadius: '12px', marginBottom: '18px' }}>
                            <p style={{ fontSize: '.83rem', color: '#374151', lineHeight: 1.6 }}>Estás volviendo al punto de venta general. Asegúrate de haber <strong>cerrado la sesión POS</strong> correctamente para evitar inconsistencias en los movimientos de caja.</p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setShowLogoutModal(false)} style={{ flex: 1, padding: '10px', borderRadius: '11px', border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#374151', fontWeight: '700', fontSize: '.85rem', cursor: 'pointer', transition: 'all .2s' }}
                                onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'}
                                onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'}>
                                Cancelar
                            </button>
                            <button onClick={() => { setShowLogoutModal(false); navigate('/pos-list'); }} style={{ flex: 1, padding: '10px', borderRadius: '11px', border: 'none', background: `linear-gradient(135deg,${C.secondary},${C.secondaryDark})`, color: '#fff', fontWeight: '800', fontSize: '.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', boxShadow: `0 4px 14px ${C.secondary}30`, transition: 'all .2s' }}
                                onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'}
                                onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'}>
                                <ArrowLeft size={13} /> Ir a POS
                            </button>
                        </div>
                    </div>
                </PosModal>
            )}

            {/* Estilos hover para tooltip */}
            <style>{`
                .nbp-nav-btn:hover .nbp-tooltip { opacity:1 !important; }
                .nbp .group:hover .nbp-tooltip { opacity:1 !important; }
            `}</style>
        </>
    );
};

export default NavbarPos;

